import { useState } from "react";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { Button } from "../../components/button.js";
import type { WizardState, WcagLevel } from "../../api/types.js";

interface WcagOption {
  value: WcagLevel;
  title: string;
  badge: string;
  description: string;
  recommended?: boolean;
}

const OPTIONS: WcagOption[] = [
  {
    value: "WCAG21AA",
    title: "WCAG 2.1 Level AA",
    badge: "Standard",
    description:
      "The current legal baseline in most jurisdictions. Covers the most widely-required accessibility criteria. Recommended for public sector and regulated industry sites.",
    recommended: true,
  },
  {
    value: "WCAG22AA",
    title: "WCAG 2.2 Level AA",
    badge: "Latest",
    description:
      "The latest W3C standard, adding nine new success criteria to WCAG 2.1 AA. Best for organisations aiming ahead of upcoming legal updates.",
  },
];

interface WcagStandardStepProps {
  websiteId: string;
  wizardState: WizardState;
}

export function WcagStandardStep({
  websiteId,
  wizardState,
}: WcagStandardStepProps) {
  const existingData = wizardState.steps.find(
    (s) => s.step === "wcag_standard",
  )?.data;
  const existing =
    (existingData?.wcagLevel as WcagLevel | undefined) ?? "WCAG21AA";

  const [selected, setSelected] = useState<WcagLevel>(existing);
  const advance = useAdvanceWizardStep(websiteId);

  function handleContinue() {
    advance.mutate({
      step: "wcag_standard",
      outcome: "pass",
      data: { wcagLevel: selected },
    });
  }

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Accessibility standard</h2>
      <p className="wizard-content__subtitle">
        Which version of WCAG should AccessiMonitor scan against? This
        determines which success criteria your site is tested on.
      </p>

      <div className="option-grid">
        {OPTIONS.map((opt) => (
          <div
            key={opt.value}
            className={`option-card${selected === opt.value ? " option-card--selected" : ""}${opt.recommended ? " option-card--recommended" : ""}`}
            onClick={() => setSelected(opt.value)}
            role="radio"
            aria-checked={selected === opt.value}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelected(opt.value);
            }}
          >
            <p className="option-card__title">{opt.title}</p>
            <p className="option-card__desc">{opt.description}</p>
          </div>
        ))}
      </div>

      <p className="text-sm text-muted">
        You can change this later. WCAG 2.1 AA is sufficient for most legal
        requirements today.
      </p>

      {advance.error && (
        <div className="alert alert--error mt-3">{advance.error.message}</div>
      )}

      <div className="wizard-content__actions">
        <Button onClick={handleContinue} loading={advance.isPending}>
          Continue
        </Button>
      </div>
    </div>
  );
}
